// src/components/doctor/SafetyStrip.jsx
//
// Tier 1 of the doctor's patient record — always in view, under the hero, on
// every tab: Allergies · Active medicines · Latest vitals. These are the three
// facts a doctor must not prescribe without. Each tile is honest about what it
// does NOT know:
//   - allergies: text / "No allergies recorded" / "not available" are three
//     different things (utils/allergyState.js);
//   - active medicines: computed from SIGNED Previu prescriptions only, and the
//     tile says so; a failed prescriptions call is "couldn't load", never "none";
//   - vitals: an old reading is labelled "Last recorded · <date>", never "latest".

import React from 'react';
import { allergyState } from '../../utils/allergyState';
import { activeMedicinesSummary, fmtDayShort } from '../../utils/activeMedicines';
import { newestFirst, latestVitalsHeading, sourcePhrase } from '../../utils/vitalsTrend';
import { vitalChips, unplacedFlags } from '../../utils/consultVitals';

function Loading() {
  return <div className="skeleton skeleton--row" aria-label="Loading" />;
}

function Failed({ what, onRetry }) {
  return (
    <div className="consult-tile__m">
      Couldn’t load {what}.{' '}
      <button type="button" className="consult-link" onClick={onRetry}>Retry</button>
    </div>
  );
}

function AllergyTile({ patient }) {
  const a = allergyState(patient);
  const warn = a.kind !== 'none';
  return (
    <div className={`consult-tile consult-tile--allergy${warn ? ' consult-tile--warn' : ''}`} data-testid="tile-allergies" role="note">
      <div className="consult-tile__k"><span aria-hidden="true">⚠ </span>Allergies</div>
      <div className="consult-tile__v">{a.text}</div>
      {a.kind === 'text' && <div className="consult-tile__m">Recorded by patient</div>}
    </div>
  );
}

function MedicinesTile({ prescriptions, onOpenVisits, today }) {
  const { status } = prescriptions;
  const s = activeMedicinesSummary(prescriptions.data, today);
  return (
    <div className="consult-tile" data-testid="tile-active-medicines">
      <div className="consult-tile__k">Active medicines{status === 'ready' && s.count > 0 ? ` · ${s.count}` : ''}</div>
      {status === 'loading' || status === 'idle' ? <Loading />
        : status === 'error' ? <Failed what="prescriptions" onRetry={prescriptions.reload} />
          : s.count === 0 ? (
            <>
              <div className="consult-tile__v prec-muted-v">No active medicines from Previu prescriptions</div>
              <div className="consult-tile__m">
                Only prescriptions signed in Previu are counted ·{' '}
                <button type="button" className="consult-link" onClick={onOpenVisits}>History</button>
              </div>
            </>
          ) : (
            <>
              <div className="consult-tile__v">{s.names.join(' · ')}{s.more > 0 ? ` · +${s.more}` : ''}</div>
              <div className="consult-tile__m">
                Earliest course ends {fmtDayShort(s.earliestEnd)} · from signed Previu prescriptions ·{' '}
                <button type="button" className="consult-link" onClick={onOpenVisits}>History</button>
              </div>
            </>
          )}
    </div>
  );
}

function VitalsTile({ vitals, onOpenVitals, today }) {
  const latest = newestFirst(vitals.data)[0] || null;
  const heading = latestVitalsHeading(latest, today);
  const chips = vitalChips(latest);
  const source = latest ? sourcePhrase(latest.source) : '';
  return (
    <div className="consult-tile consult-tile--vitals" data-testid="tile-latest-vitals">
      <div className="consult-tile__k">{heading.label}</div>
      {vitals.status === 'loading' || vitals.status === 'idle' ? <Loading />
        : vitals.status === 'error' ? <Failed what="vitals" onRetry={vitals.reload} />
          : chips.length === 0 ? <div className="consult-tile__v prec-muted-v">No vitals recorded yet</div>
            : (
              <>
                <div className="consult-chips">
                  {chips.map(c => (
                    <span key={c.key} className={`consult-chip${c.flagged ? ' consult-chip--flag' : ''}`} title={c.flagText || undefined}>
                      {c.text}
                      {c.flagged && <><span aria-hidden="true">{c.arrow}</span><span className="sr-only"> ({c.flagText})</span></>}
                    </span>
                  ))}
                  {unplacedFlags(latest).map(t => <span key={t} className="consult-chip consult-chip--flag">{t}</span>)}
                </div>
                <div className="consult-tile__m">
                  {source && <>{source} · </>}
                  <button type="button" className="consult-link" onClick={onOpenVitals}>Trends →</button>
                </div>
              </>
            )}
    </div>
  );
}

export default function SafetyStrip({
  patient, vitals, prescriptions, onOpenVisits, onOpenVitals, today = new Date(),
}) {
  return (
    <section className="prec-safety fade-in-up" aria-label="Safety summary" data-testid="safety-strip">
      <AllergyTile patient={patient} />
      <MedicinesTile prescriptions={prescriptions} onOpenVisits={onOpenVisits} today={today} />
      <VitalsTile vitals={vitals} onOpenVitals={onOpenVitals} today={today} />
    </section>
  );
}
